/**
 * DualApp — Supports both Okta and Cognito authentication simultaneously.
 *
 * - Configures Amplify for Cognito (email/password for external users)
 * - Configures Okta Security provider (SSO for @nepgroup.com users)
 * - Wraps app in both auth providers
 */

import { useNavigate } from "react-router-dom";
import { Amplify } from "aws-amplify";
import { OktaAuth, toRelativeUrl } from "@okta/okta-auth-js";
import { Security, LoginCallback } from "@okta/okta-react";
import { Route, Routes } from "react-router-dom";
import config from "./config";
import { oktaConfig } from "./oktaConfig";
import { DualAuthProvider } from "./contexts/DualAuthContext";
import { RequiredAuth } from "./components/RequiredAuth";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import InviteAcceptPage from "./pages/InviteAcceptPage";
import App from "./App";

// Configure Amplify for Cognito
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: config.cognito.USER_POOL_ID,
      userPoolClientId: config.cognito.APP_CLIENT_ID,
    },
  },
});

// Configure Okta
const oktaAuth = new OktaAuth(oktaConfig);

function DualAppInner() {
  const navigate = useNavigate();

  const restoreOriginalUri = (_oktaAuth: OktaAuth, originalUri: string) => {
    navigate(toRelativeUrl(originalUri || "/", window.location.origin));
  };

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <DualAuthProvider>
        <Routes>
          {/* Okta login callback */}
          <Route path="/login/callback" element={<LoginCallback />} />

          {/* Login page (public) */}
          <Route path="/login" element={<LoginPage />} />

          {/* Signup page (public) */}
          <Route path="/signup" element={<SignupPage />} />

          {/* Forgot password (public) */}
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />

          {/* Guest invite acceptance (public) */}
          <Route path="/invite/:inviteId" element={<InviteAcceptPage />} />

          {/* All other routes require authentication */}
          <Route element={<RequiredAuth />}>
            <Route path="/*" element={<App />} />
          </Route>
        </Routes>
      </DualAuthProvider>
    </Security>
  );
}

export default function DualApp() {
  return <DualAppInner />;
}
